import { MainContainer, SelectDropDown } from '@wot/components';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  endOfYear,
  parseISO,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths
} from 'date-fns';
import { format } from 'date-fns/esm';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { GREEN_ARROW_UP, RED_ARROW_DOWN } from '../../../../assets/icons';
import Slider from '../../../../components/Slider';
import xhr from '../../../../utils/fetch';
import { currencyFormatter, increasedPrecentage } from '../../../../utils/functions';

export default function TutorEarnings() {
  const [chartData, setChartData] = useState<{ label: string; Earnings: number; Expenses: number }[]>([]);
  const [range, setRange] = useState('This Week');
  const [maxEarnings, setmaxEarnings] = useState<{ month: string; total: number }>();

  const [totalSessions, setTotalSessions] = useState(0);
  const [currentMonthTotalSessions, setCurrentMonthTotalSessions] = useState(0);
  const [lastMonthTotalSessions, setLastMonthTotalSessions] = useState(0);
  const [estimatedEarnings, setEstimatedEarnings] = useState(0);
  const [actualEarnings, setActualEarnings] = useState(0);
  const [lastMonthActualEarnings, setLastMonthActualEarnings] = useState(0);
  const [currentMonthDeductions, setCurrentMonthDeductions] = useState(0);
  const [lastMonthDeductions, setLastMonthDeductions] = useState(0);
  const [currentMonth] = useState(new Date());
  const { tutor_id } = useParams();

  const getEarnings = async (start: string, end: string) => {
    return await xhr({
      url: `/t/${tutor_id}/earnings?startAfter=${start}&endBefore=${end}`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          return res.data;
        } else {
          return null;
        }
      })
      .catch((err) => err);
  };

  const getTotalSessions = async (start?: string, end?: string) => {
    return await xhr({
      url: `/t/${tutor_id}/sessions-count`,
      method: 'GET',
      params: {
        startAfter: start || '',
        endBefore: end || ''
      }
    }).then((res) => {
      if (res.status === 'OK') {
        return res.data.count || 0;
      }
    });
  };

  function daysInMonth(date: Date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  }

  useEffect(() => {
    if (range === 'This Week') {
      let start = startOfWeek(new Date()).toString();
      let end = endOfWeek(new Date()).toString();
      getTotalSessions(start, end).then((data) => setTotalSessions(data));
      getEarnings(start, end).then((res) => {
        let temp: any = [];
        let weekDay = startOfWeek(new Date());
        [...new Array(7)].map((_, i) => {
          let day = format(addDays(weekDay, i), 'EEE');
          let date = format(addDays(weekDay, i), 'd');
          const v = res.find((item: any) => format(parseISO(item.date), 'd') === date);
          temp.push({
            label: day,
            Earnings: (v && v.total) || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
    if (range === 'This Month') {
      let start = startOfMonth(new Date()).toString();
      let end = endOfMonth(new Date()).toString();
      getTotalSessions(start, end).then((data) => setTotalSessions(data));
      getEarnings(start, end).then((res) => {
        let temp: any = [];
        let monthDay = startOfMonth(new Date());
        [...new Array(daysInMonth(new Date()))].map((_, i) => {
          let day = format(addDays(monthDay, i), 'LLL d');
          let date = format(addDays(monthDay, i), 'd');
          const v = res.find((item: any) => format(parseISO(item.date), 'd') === date);
          temp.push({
            label: day,
            Earnings: (v && v.total) || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
    if (range === 'Last Month') {
      let month = subMonths(new Date(), 1);
      let start = startOfMonth(month).toString();
      let end = endOfMonth(month).toString();
      getTotalSessions(start, end).then((data) => setTotalSessions(data));
      getEarnings(start, end).then((res) => {
        let temp: any = [];
        let monthDay = startOfMonth(month);
        [...new Array(daysInMonth(month))].map((_, i) => {
          let day = format(addDays(monthDay, i), 'LLL d');
          let date = format(addDays(monthDay, i), 'd');
          const v = res.find((item: any) => format(parseISO(item.date), 'd') === date);
          temp.push({
            label: day,
            Earnings: (v && v.total) || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
    if (range === 'This Year') {
      let start = startOfYear(new Date()).toString();
      let end = endOfYear(new Date()).toString();
      getTotalSessions(start, end).then((data) => setTotalSessions(data));
      getEarnings(start, end).then((res) => {
        let temp: any = [];
        let yearDay = startOfYear(new Date());
        [...new Array(12)].map((_, i) => {
          let day = format(addMonths(yearDay, i), 'LLL');
          const v = res.reduce((acc: any, currValue: any) => {
            if (format(parseISO(currValue.date), 'LLL') === day) {
              return acc + currValue.total;
            }
          }, 0);
          temp.push({
            label: day,
            Earnings: v || 0,
            Expenses: 0
          });
        });
        setChartData(temp);
      });
    }
  }, [range]);

  // Max Earnings
  useEffect(() => {
    xhr({
      url: `/t/${tutor_id}/max-earnings`,
      method: 'GET'
    }).then((res) => {
      if (res.status === 'OK' && res.data) {
        setmaxEarnings({ month: res.data._id, total: res.data.total });
      }
    });
  }, []);

  // Estimated Earnings
  useEffect(() => {
    xhr({
      url: `/t/${tutor_id}/estimated-earnings`,
      method: 'GET',
      params: {
        startAfter: startOfMonth(currentMonth).toString(),
        endBefore: endOfMonth(currentMonth).toString()
      }
    }).then((res) => {
      if (res.status === 'OK') {
        if (res.data) {
          setEstimatedEarnings(res.data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
        } else {
          setEstimatedEarnings(0);
        }
      }
    });
  }, []);

  // Actual Earnings And Last month earnings
  useEffect(() => {
    getEarnings(startOfMonth(currentMonth).toString(), endOfMonth(currentMonth).toString()).then((res) => {
      if (res.status === 'OK') {
        setActualEarnings(res.data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
      }
    });
    getEarnings(
      startOfMonth(subMonths(currentMonth, 1)).toString(),
      endOfMonth(subMonths(currentMonth, 1)).toString()
    ).then((res) => {
      if (res.status === 'OK') {
        if (res.data) {
          setLastMonthActualEarnings(res.data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
        }
      }
    });
  }, []);

  // Deductions Earnings
  useEffect(() => {
    xhr({
      url: `/t/${tutor_id}/deductions`,
      method: 'GET',
      params: {
        startAfter: startOfMonth(currentMonth).toString(),
        endBefore: endOfMonth(currentMonth).toString()
      }
    }).then((res) => {
      if (res.status === 'OK') {
        setCurrentMonthDeductions(res.data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
      }
    });
    xhr({
      url: `/t/${tutor_id}/deductions`,
      method: 'GET',
      params: {
        startAfter: startOfMonth(subMonths(currentMonth, 1)).toString(),
        endBefore: endOfMonth(subMonths(currentMonth, 1)).toString()
      }
    }).then((res) => {
      if (res.status === 'OK') {
        if (res.data) {
          setLastMonthDeductions(res.data.reduce((acc: any, curr: any) => (acc += curr.total), 0));
        }
      }
    });
  }, []);

  // Last Month Classes
  useEffect(() => {
    getTotalSessions(startOfMonth(currentMonth).toString(), endOfMonth(currentMonth).toString()).then((data) => {
      setCurrentMonthTotalSessions(data);
    });
    getTotalSessions(
      startOfMonth(subMonths(currentMonth, 1)).toString(),
      endOfMonth(subMonths(currentMonth, 1)).toString()
    ).then((data) => {
      setLastMonthTotalSessions(data);
    });
  }, []);

  return (
      <div className="w-full flex flex-col gap-4 p-8 grow bg-white rounded-xl">
        <div className="w-full flex justify-between">
          <h1 className="text-2xl font-semibold">My Earnings</h1>
        </div>
        <div className="w-full grid  grid-cols-1 lg:grid-cols-4 grid-rows-2 gap-4 lg:p-4 lg:border lg:border-primary rounded-md">
          <div className="w-full flex flex-col p-8 gap-5 row-span-2 col-span-2 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <h1 className="text-base font-normal">Total</h1>
                <div className="w-full max-w-[164px]">
                  <SelectDropDown
                    placeholder="Select Range"
                    value={range}
                    onChange={(e) => setRange(e)}
                    options={['This Week', 'This Month', 'Last Month', 'This Year']}
                  />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-primary">
                INR {chartData.reduce((acc, currValue) => acc + currValue.Earnings, 0).toFixed(2)}
              </h1>
            </div>

            <div className="w-full h-[200px] mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  width={730}
                  height={164}
                  data={chartData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5
                  }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="1" />
                  <XAxis dataKey="label" fontSize={12} />
                  <YAxis axisLine={false} width={20} fontSize={12} orientation="right" />
                  <Tooltip />
                  <Bar dataKey="Earnings" fill="#8884d8" barSize={10}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill="#EA4459" strokeLinecap="round" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="flex flex-col gap-2">
              <h2 className="text-sm font-medium">Total Classes taken</h2>
              <h1 className="text-2xl font-bold">{totalSessions}</h1>
            </div>

            <div className="w-full flex justify-between gap-4">
              <div className="flex flex-col gap-2">
                <h2 className="text-sm font-medium">Previous Earning. </h2>
                <h1 className="text-2xl font-bold">INR {currencyFormatter(lastMonthActualEarnings)}</h1>
              </div>

              <div className="flex flex-col gap-2">
                <h2 className="text-sm font-medium">Maximum earning</h2>
                <h1 className="text-2xl font-bold">INR {currencyFormatter(maxEarnings?.total || 0)}</h1>
              </div>
            </div>
          </div>

          <div className="w-full hidden lg:flex flex-col gap-4 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
            <p className="w-full text-sm text-right">{format(currentMonth, 'LLLL')}</p>
            <h3 className="text-2xl font-medium">Estimated earning this month</h3>
            <h1 className="text-2xl font-bold">INR {currencyFormatter(estimatedEarnings)}</h1>
          </div>

          <div className="w-full hidden lg:flex flex-col gap-4 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
            <p className="w-full text-sm text-right">{format(currentMonth, 'LLLL')}</p>
            <h3 className="text-2xl font-medium">Classes taken percentage</h3>
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold">
                {' '}
                {lastMonthTotalSessions >= currentMonthTotalSessions ? '-' : '+'}
                {increasedPrecentage(lastMonthTotalSessions, currentMonthTotalSessions)}%
              </h1>
              {lastMonthTotalSessions > currentMonthTotalSessions ? (
                <img src={RED_ARROW_DOWN} className="w-6 h-6" />
              ) : (
                <img src={GREEN_ARROW_UP} className="w-6 h-6" />
              )}
            </div>
          </div>

          <div className="w-full hidden lg:flex flex-col gap-4 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
            <p className="w-full text-sm text-right">{format(currentMonth, 'LLLL')}</p>
            <h3 className="text-2xl font-medium">Actual Earning this month</h3>
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold">INR {currencyFormatter(actualEarnings)}</h1>
              <div className="flex items-center gap-2">
                {lastMonthActualEarnings > actualEarnings ? (
                  <>
                    <p className="text-red-500 text-sm">
                      {increasedPrecentage(lastMonthActualEarnings, actualEarnings)}%
                    </p>
                    <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                  </>
                ) : (
                  <>
                    <p className="text-green-500 text-sm">
                      {increasedPrecentage(lastMonthActualEarnings, actualEarnings)}%
                    </p>
                    <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="w-full hidden lg:flex flex-col gap-4 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
            <p className="w-full text-sm text-right">{format(currentMonth, 'LLLL')}</p>
            <h3 className="text-2xl font-medium">Deductions this month</h3>
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold">INR {currencyFormatter(currentMonthDeductions)}</h1>
              <div className="flex items-center gap-2">
                {lastMonthDeductions > currentMonthDeductions ? (
                  <>
                    <p className="text-green-500 text-sm">
                      {increasedPrecentage(lastMonthDeductions, currentMonthDeductions)}%
                    </p>
                    <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                  </>
                ) : (
                  <>
                    <p className="text-red-500 text-sm">
                      {increasedPrecentage(lastMonthDeductions, currentMonthDeductions)}%
                    </p>
                    <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                  </>
                )}
              </div>
            </div>
            <p className="text-sm ">(as compared to previous month)</p>
          </div>
          <div className="lg:hidden">
            <Slider>
              <div className="min-w-[256px] w-full flex flex-col gap-4 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                <p className="w-full text-sm text-right">{format(currentMonth, 'LLLL')}</p>
                <h3 className="text-2xl font-medium">Estimated earning this month</h3>
                <h1 className="text-2xl font-bold">INR {currencyFormatter(estimatedEarnings)}</h1>
              </div>

              <div className="min-w-[256px] w-full flex flex-col gap-4 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                <p className="w-full text-sm text-right">{format(currentMonth, 'LLLL')}</p>
                <h3 className="text-2xl font-medium">Classes taken percentage</h3>
                <div className="flex items-center gap-4">
                  <h1 className="text-2xl font-bold">
                    {' '}
                    {lastMonthTotalSessions >= currentMonthTotalSessions ? '-' : '+'}
                    {increasedPrecentage(lastMonthTotalSessions, currentMonthTotalSessions)}%
                  </h1>
                  {lastMonthTotalSessions > currentMonthTotalSessions ? (
                    <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                  ) : (
                    <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                  )}
                </div>
              </div>

              <div className="min-w-[256px] w-full flex flex-col gap-4 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                <p className="w-full text-sm text-right">{format(currentMonth, 'LLLL')}</p>
                <h3 className="text-2xl font-medium">Actual Earning this month</h3>
                <div className="flex items-center gap-4">
                  <h1 className="text-2xl font-bold">INR {currencyFormatter(actualEarnings)}</h1>
                  <div className="flex items-center gap-2">
                    {lastMonthActualEarnings > actualEarnings ? (
                      <>
                        <p className="text-red-500 text-sm">
                          {increasedPrecentage(lastMonthActualEarnings, actualEarnings)}%
                        </p>
                        <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                      </>
                    ) : (
                      <>
                        <p className="text-green-500 text-sm">
                          {increasedPrecentage(lastMonthActualEarnings, actualEarnings)}%
                        </p>
                        <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="min-w-[256px] w-full flex flex-col gap-4 p-4 rounded-md border border-neutralBlue1 bg-white drop-shadow-md">
                <p className="w-full text-sm text-right">{format(currentMonth, 'LLLL')}</p>
                <h3 className="text-2xl font-medium">Deductions this month</h3>
                <div className="flex items-center gap-4">
                  <h1 className="text-2xl font-bold">INR {currencyFormatter(currentMonthDeductions)}</h1>
                  <div className="flex items-center gap-2">
                    {lastMonthDeductions > currentMonthDeductions ? (
                      <>
                        <p className="text-green-500 text-sm">
                          {increasedPrecentage(lastMonthDeductions, currentMonthDeductions)}%
                        </p>
                        <img src={GREEN_ARROW_UP} className="w-6 h-6" />
                      </>
                    ) : (
                      <>
                        <p className="text-red-500 text-sm">
                          {increasedPrecentage(lastMonthDeductions, currentMonthDeductions)}%
                        </p>
                        <img src={RED_ARROW_DOWN} className="w-6 h-6" />
                      </>
                    )}
                  </div>
                </div>
                <p className="text-sm ">(as compared to previous month)</p>
              </div>
            </Slider>
          </div>
        </div>
      </div>
  );
}
